import React, { useCallback, useState } from 'react';
import { HelperText, useTheme } from 'react-native-paper';
import { CompositeScreenProps } from '@react-navigation/native';
import { StackScreenProps } from '@react-navigation/stack';
import { useMutation, useQueryClient } from 'react-query';
import { useI18n } from '../../context/I18nContext';
import ConfirmationRow from '../../payment/components/ConfirmationRow';
import Container from '../../components/Container';
import { NavigationDialog } from '../CommonDialogs';
import { AppDrawerScreenProp } from '../../components/DrawerMenuContent';
import { useMixpanel } from '../../mixpanel/MixpanelContext';
import Spacer from '../../components/Spacer';
import { cancelSubscription, invalidateSubscriptionData, useGetProductSubscription } from '../../apis/subscriptionApis';
import { flattenDict } from '../../utils/helpers';
import { MapStackRouteParams } from '../../navigators/MapStackNavigator';
import Typography from '../../components/Typography';
import { ServiceType } from '../../types/appsync-types';

export interface ConfirmReservationProductEndDialogRouteParams {
  id: string;
}

type Props = CompositeScreenProps<
  StackScreenProps<MapStackRouteParams, 'ConfirmReservationProductEndDialog'>,
  AppDrawerScreenProp
>;

export function ConfirmReservationProductEndDialog({ navigation, route }: Props) {
  const { colors } = useTheme();
  const mp = useMixpanel();
  const queryClient = useQueryClient();
  const { I18n } = useI18n();
  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  // TODO: Check if this is getting the subscription from cache or fetching it from backend again, cache would be preferred
  const getProductSubscriptionResult = useGetProductSubscription(route.params.id);

  // TODO: handle either of these being undefined
  const subscription = getProductSubscriptionResult.data?.data?.getProductSubscription;
  const product = subscription?.product;

  const isLockerProduct = product?.serviceType === ServiceType.LOCKER;
  const serviceTypeSpecificLocalizationPath = isLockerProduct
    ? 'lockerInfo.reservation.confirmReservationProductEndModal'
    : 'reservation.confirmReservationProductEndModal';

  const cancelSubscriptionMutation = useMutation(
    async (input: Parameters<typeof cancelSubscription>) => {
      return cancelSubscription(...input);
    },
    {
      onSuccess(data) {
        mp?.track('Product reservation end success', flattenDict('subscription', data.data?.makeAProductSubscription));
        mp?.getPeople().increment('Product reservations ended', 1);
        // Sparing a bit time to try to eliminate the async update of subscription
        setTimeout(() => {
          invalidateSubscriptionData(queryClient);
        }, 1000);
        navigation.goBack();
      },
      onError(error: any) {
        mp?.track('Product reservation end error', error ?? null);
        setErrorMessage(`${I18n.t(serviceTypeSpecificLocalizationPath + '.failTitle')}: ${error?.message}`);
      },
    },
  );

  const handleUserDismissed = useCallback(() => {
    navigation.goBack();
  }, [navigation]);

  return (
    <NavigationDialog
      style={{ backgroundColor: colors.elevation.level2 }} // TODO: is this correct?
      title={I18n.t(`${serviceTypeSpecificLocalizationPath}.title`)}
      description={''}
      onDismiss={handleUserDismissed}
      actions={[
        {
          title: I18n.t('reservation.confirmReservationProductEndModal.cancel'),
          onPress: handleUserDismissed,
          disable: cancelSubscriptionMutation.isLoading,
        },
        {
          title: I18n.t('reservation.confirmReservationProductEndModal.confirm'),
          loading: cancelSubscriptionMutation.isLoading,
          disable: cancelSubscriptionMutation.isLoading,
          onPress: () => {
            return cancelSubscriptionMutation.mutate([{ id: route.params.id }]);
          },
        },
      ]}
      iconName={'check'}
    >
      {/* Product */}
      <ConfirmationRow
        title={product?.name ?? '?'}
        iconName={
          isLockerProduct
            ? { name: 'locker', set: 'material-community' }
            : { name: 'square-parking', set: 'fontawesome6' }
        }
      />

      <Typography variant="body1" style={{ fontWeight: 'bold', marginLeft: 8, paddingHorizontal: 16 }}>
        {I18n.t('reservation.confirmReservationProductEndModal.helpText')}
      </Typography>

      <Spacer />

      {/* Error text */}
      <Container>
        <HelperText type={'error'} visible={!!errorMessage}>
          {errorMessage}
        </HelperText>
      </Container>
    </NavigationDialog>
  );
}
